import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { toApiError } from '@/lib/plan-limit';
import type { CreateProjectInput, UpdateProjectInput, InviteMemberInput } from '@/types/project';

export function useProjects() {
  return useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await api.listProjects();
      // Collapsing a failure into [] renders the "No projects yet" empty state,
      // which is a false claim about the user's own data. Throw so the query
      // lands in its error state and the caller can offer a retry.
      if (!response.success) throw new Error(response.message || 'Failed to load projects');
      return response.data ?? [];
    },
  });
}

export function useProject(projectId: string) {
  return useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const response = await api.getProject(projectId);
      return response.success ? response.data : null;
    },
    enabled: !!projectId,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateProjectInput) => {
      const response = await api.createProject(input);
      if (!response.success || !response.data) {
        throw toApiError(response, 'Failed to create project');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      // A create consumed a project slot.
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, patch }: { projectId: string; patch: UpdateProjectInput }) => {
      const response = await api.updateProject(projectId, patch);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to update project');
      }
      return response.data;
    },
    onSuccess: (project) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      // Merge, don't replace: PATCH responses omit usage/limits, which only
      // the single-project GET carries.
      queryClient.setQueryData(['project', project.id], (prev: typeof project | undefined) =>
        prev ? { ...prev, ...project } : project,
      );
      // Idea capture writes route to a graph node now, so any
      // canvas query for this project needs to refetch to pick up the
      // new/updated idea node without a hard reload.
      queryClient.invalidateQueries({ queryKey: ['canvas', project.id] });
    },
  });
}

// Draft the respondent framing (step-1 card). Returning the invalidation
// promise keeps isPending true until the refetched project lands, so the
// card can't flash a stale state between mutate-settled and refetch.
export function useGenerateFraming(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.generateFraming(projectId);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to generate framing');
      }
      return response.data;
    },
    onSuccess: (data) =>
      data.framing
        ? queryClient.invalidateQueries({ queryKey: ['project', projectId] })
        : undefined,
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId: string) => {
      const response = await api.deleteProject(projectId);
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete project');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useProjectMembers(projectId: string) {
  return useQuery({
    queryKey: ['project-members', projectId],
    queryFn: async () => {
      const response = await api.listMembers(projectId);
      return response.success && response.data ? response.data : [];
    },
    enabled: !!projectId,
  });
}

export function useInviteMember(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: InviteMemberInput) => {
      const response = await api.inviteMember(projectId, input);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to invite member');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-members', projectId] });
    },
  });
}

export function useRemoveMember(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (email: string) => {
      const response = await api.removeMember(projectId, email);
      if (!response.success) {
        throw new Error(response.message || 'Failed to remove member');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-members', projectId] });
    },
  });
}
